"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "@/components/waves/drops/dropRankStyles";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation } from "@/components/waves/drops/drop.types";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import WaveDropActions from "@/components/waves/drops/WaveDropActions";
import MemeWinnerHeader from "./MemeWinnerHeader";
import MemeWinnerDescription from "./MemeWinnerDescription";
import MemeWinnerArtistInfo from "./MemeWinnerArtistInfo";
import MemeDropTraits from "./MemeDropTraits";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { useDropContext } from "@/components/waves/drops/DropContext";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
interface MemeWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showReplyAndQuote: boolean;
  readonly onReply: (param: DropInteractionParams) => void;
}
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
export default function MemeWinnerDrop(t0) {
  const $ = _c(47);
  const {
    drop,
    showReplyAndQuote,
    onReply
  } = t0;
  const isMobile = useIsMobileDevice();
  const {
    location
  } = useDropContext();
  const title = drop.metadata.find(_temp)?.data_value ?? "Artwork Title";
  const description = drop.metadata.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
  let t1;
  if ($[0] !== drop.parts) {
    t1 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const artworkMedia = t1;
  let t2;
  if ($[2] !== drop || $[3] !== onReply) {
    t2 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[2] = drop;
    $[3] = onReply;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleOnReply = t2;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  const t3 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-group tw-relative`;
  let t4;
  if ($[5] !== effectiveRank) {
    t4 = getRankStaticBorderClass(effectiveRank);
    $[5] = effectiveRank;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== effectiveRank) {
    t5 = getRankHoverClass(effectiveRank);
    $[7] = effectiveRank;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t4} tw-transition-all tw-duration-200 tw-ease-out ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"} ${t5}`;
  let t7;
  if ($[9] !== drop) {
    t7 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 tw-pb-3"><MemeWinnerArtistInfo drop={drop} /></div>;
    $[9] = drop;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== title) {
    t8 = <MemeWinnerHeader title={title} />;
    $[11] = title;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== description) {
    t9 = <MemeWinnerDescription description={description} />;
    $[13] = description;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div className="tw-px-4 tw-pb-4 tw-pt-4"><div className="tw-space-y-1">{t8}{t9}</div></div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== drop) {
    t11 = <WaveWinnerIdentity drop={drop} variant="full" className="tw-px-4 tw-pb-4" />;
    $[18] = drop;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== artworkMedia) {
    t12 = artworkMedia && <div className="tw-mx-0.5 tw-flex tw-h-96 tw-justify-center tw-bg-iron-950"><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[20] = artworkMedia;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== drop) {
    t13 = <div className="tw-mt-4 tw-hidden tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/5 tw-bg-iron-900/30 tw-p-4 lg:tw-block"><MemeDropTraits drop={drop} /></div>;
    $[22] = drop;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t7) {
    t14 = <>{t7}{t10}{t11}{t12}{t13}</>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t7;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== drop || $[31] !== handleOnReply || $[32] !== showReplyAndQuote || $[33] !== t14) {
    t15 = <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{t14}</DropMobileMenuHandler>;
    $[30] = drop;
    $[31] = handleOnReply;
    $[32] = showReplyAndQuote;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== drop || $[36] !== handleOnReply || $[37] !== isMobile || $[38] !== showReplyAndQuote) {
    t16 = !isMobile && showReplyAndQuote && <div className="tw-absolute tw-right-4 tw-top-2"><WaveDropActions drop={drop} activePartIndex={0} onReply={handleOnReply} /></div>;
    $[35] = drop;
    $[36] = handleOnReply;
    $[37] = isMobile;
    $[38] = showReplyAndQuote;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t6) {
    t17 = <div className={t6}>{t15}{t16}</div>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t6;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t17 || $[45] !== t3) {
    t18 = <div className="tw-mb-3 tw-w-full"><div className={t3}>{t17}</div></div>;
    $[44] = t17;
    $[45] = t3;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}